/**
 * DailyLogDrawer Component
 * Compact, ultra-clean Daily Log form
 * BuilderBel Construction Project Management System
 */

import { yupResolver } from '@hookform/resolvers/yup';
import { Calendar, CheckCircle, FileDown, Send, X, XCircle } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../hooks/useAuth';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';
import {
  useApproveDailyLogMutation,
  useCreateDailyLogMutation,
  useRejectDailyLogMutation,
  useSubmitDailyLogMutation,
  useUpdateDailyLogMutation,
  downloadDailyLogPdf,
} from '../../services/dailyLogApi';
import type {
  DailyLog,
  DailyLogStatus,
  CreateDailyLogRequest,
  UpdateDailyLogRequest,
  WeatherEntry,
  WorkforceEntry,
  EquipmentEntry,
  WorkEntry,
  DelayEntry,
  VisitorEntry,
  DeliveryEntry,
  SafetyEntry,
  DailyLogPhoto,
} from '../../types/dailyLog';
import { DAILY_LOG_STATUS_LABELS } from '../../types/dailyLog';
import { DatePicker } from '../forms';
import SignatureCapture from './SignatureCapture';
import {
  WeatherSection,
  WorkforceSection,
  EquipmentSection,
  WorkPerformedSection,
  DelaysSection,
  VisitorsSection,
  DeliveriesSection,
  SafetySection,
  PhotosSection,
  NotesSection,
} from './sections';

// Props interface
export interface DailyLogDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  dailyLog?: DailyLog | null;
  mode: 'create' | 'edit' | 'view';
  onSuccess?: () => void;
  defaultDate?: string;
}

// Form data type
interface DailyLogFormData {
  logDate: string;
  weather: WeatherEntry;
  workforce: WorkforceEntry[];
  equipment: EquipmentEntry[];
  workPerformed: WorkEntry[];
  delays: DelayEntry[];
  visitors: VisitorEntry[];
  deliveries: DeliveryEntry[];
  safety: Partial<SafetyEntry>;
  photos: Partial<DailyLogPhoto>[];
  generalNotes: string;
  issuesAndConcerns: string;
  tomorrowPlan: string;
}

// Validation schema
const dailyLogSchema = Yup.object({
  logDate: Yup.string().required('Log date is required'),
  weather: Yup.object({
    conditions: Yup.string().required('Weather conditions required'),
    temperatureHigh: Yup.number().required('High temperature required'),
    temperatureLow: Yup.number().required('Low temperature required'),
    temperatureUnit: Yup.string().oneOf(['F', 'C']).required(),
    humidity: Yup.number().min(0).max(100).nullable(),
    windSpeed: Yup.number().min(0).nullable(),
    precipitation: Yup.number().min(0).nullable(),
    precipitationType: Yup.string().oneOf(['none', 'rain', 'snow', 'sleet', 'hail']).required(),
    delayHours: Yup.number().min(0).max(24).required(),
    notes: Yup.string().nullable(),
  }),
  workforce: Yup.array().of(
    Yup.object({
      trade: Yup.string().required('Trade is required'),
      company: Yup.string().required('Company is required'),
      workerCount: Yup.number().required().min(1, 'At least 1 worker'),
      hours: Yup.number().required().min(0.5, 'At least 0.5 hours'),
      workArea: Yup.string().nullable(),
      supervisor: Yup.string().nullable(),
      notes: Yup.string().nullable(),
    })
  ),
  generalNotes: Yup.string().nullable(),
  issuesAndConcerns: Yup.string().nullable(),
  tomorrowPlan: Yup.string().nullable(),
});

// Status colors
const STATUS_COLORS: Record<DailyLogStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

// Get today's date in ISO format
const getTodayISO = (): string => new Date().toISOString().split('T')[0];

// Format date for display
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const DailyLogDrawer: React.FC<DailyLogDrawerProps> = ({
  isOpen,
  onClose,
  dailyLog,
  mode,
  onSuccess,
  defaultDate,
}) => {
  const projectId = useCurrentProjectId();
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  // State
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureAction, setSignatureAction] = useState<'submit' | 'approve'>('submit');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Collapsible sections - start compact for create mode
  const [expandedSections, setExpandedSections] = useState({
    weather: true,
    workforce: isCreateMode,
    equipment: false,
    workPerformed: false,
    delays: false,
    visitors: false,
    deliveries: false,
    safety: false,
    photos: false,
    notes: false,
  });

  // API mutations
  const [createLog, { isLoading: isCreating }] = useCreateDailyLogMutation();
  const [updateLog, { isLoading: isUpdating }] = useUpdateDailyLogMutation();
  const [submitLog, { isLoading: isSubmitting }] = useSubmitDailyLogMutation();
  const [approveLog, { isLoading: isApproving }] = useApproveDailyLogMutation();
  const [rejectLog, { isLoading: isRejecting }] = useRejectDailyLogMutation();

  const isSaving = isCreating || isUpdating;
  const isProcessing = isSaving || isSubmitting || isApproving || isRejecting;

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<DailyLogFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(dailyLogSchema) as any,
    defaultValues: {
      logDate: defaultDate || getTodayISO(),
      weather: {
        conditions: 'sunny',
        temperatureHigh: 75,
        temperatureLow: 55,
        temperatureUnit: 'F',
        precipitationType: 'none',
        delayHours: 0,
      },
      workforce: [],
      equipment: [],
      workPerformed: [],
      delays: [],
      visitors: [],
      deliveries: [],
      safety: {
        toolboxTalkConducted: false,
        observations: [],
        hazardsIdentified: [],
        correctiveActions: [],
        nearMisses: [],
        incidentReportIds: [],
        ppeCompliance: true,
        siteInspectionCompleted: false,
      },
      photos: [],
      generalNotes: '',
      issuesAndConcerns: '',
      tomorrowPlan: '',
    },
  });

  // Field arrays
  const workforceFieldArray = useFieldArray({ control, name: 'workforce' });
  const equipmentFieldArray = useFieldArray({ control, name: 'equipment' });
  const workPerformedFieldArray = useFieldArray({ control, name: 'workPerformed' });
  const delaysFieldArray = useFieldArray({ control, name: 'delays' });
  const visitorsFieldArray = useFieldArray({ control, name: 'visitors' });
  const deliveriesFieldArray = useFieldArray({ control, name: 'deliveries' });
  const photosFieldArray = useFieldArray({ control, name: 'photos' });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Reset form when dailyLog changes
  useEffect(() => {
    if (dailyLog && (isEditMode || isViewMode)) {
      reset({
        logDate: dailyLog.logDate.split('T')[0],
        weather: dailyLog.weather,
        workforce: dailyLog.workforce || [],
        equipment: dailyLog.equipment || [],
        workPerformed: dailyLog.workPerformed || [],
        delays: dailyLog.delays || [],
        visitors: dailyLog.visitors || [],
        deliveries: dailyLog.deliveries || [],
        safety: dailyLog.safety || {
          toolboxTalkConducted: false,
          observations: [],
          hazardsIdentified: [],
          correctiveActions: [],
          nearMisses: [],
          incidentReportIds: [],
          ppeCompliance: true,
          siteInspectionCompleted: false,
        },
        photos: dailyLog.photos || [],
        generalNotes: dailyLog.generalNotes || '',
        issuesAndConcerns: dailyLog.issuesAndConcerns || '',
        tomorrowPlan: dailyLog.tomorrowPlan || '',
      });
      // Expand more sections when viewing/editing existing log
      setExpandedSections({
        weather: true,
        workforce: true,
        equipment: (dailyLog.equipment?.length || 0) > 0,
        workPerformed: (dailyLog.workPerformed?.length || 0) > 0,
        delays: (dailyLog.delays?.length || 0) > 0,
        visitors: (dailyLog.visitors?.length || 0) > 0,
        deliveries: (dailyLog.deliveries?.length || 0) > 0,
        safety: dailyLog.safety?.toolboxTalkConducted || false,
        photos: (dailyLog.photos?.length || 0) > 0,
        notes: !!(dailyLog.generalNotes || dailyLog.issuesAndConcerns || dailyLog.tomorrowPlan),
      });
    } else if (isCreateMode) {
      reset({
        logDate: defaultDate || getTodayISO(),
        weather: {
          conditions: 'sunny',
          temperatureHigh: 75,
          temperatureLow: 55,
          temperatureUnit: 'F',
          precipitationType: 'none',
          delayHours: 0,
        },
        workforce: [],
        equipment: [],
        workPerformed: [],
        delays: [],
        visitors: [],
        deliveries: [],
        safety: {
          toolboxTalkConducted: false,
          observations: [],
          hazardsIdentified: [],
          correctiveActions: [],
          nearMisses: [],
          incidentReportIds: [],
          ppeCompliance: true,
          siteInspectionCompleted: false,
        },
        photos: [],
        generalNotes: '',
        issuesAndConcerns: '',
        tomorrowPlan: '',
      });
      // Compact view for create
      setExpandedSections({
        weather: true,
        workforce: true,
        equipment: false,
        workPerformed: false,
        delays: false,
        visitors: false,
        deliveries: false,
        safety: false,
        photos: false,
        notes: false,
      });
    }
  }, [dailyLog, mode, reset, isEditMode, isViewMode, isCreateMode, defaultDate]);

  // Calculate totals
  const watchedWorkforce = watch('workforce');
  const watchedDelays = watch('delays');
  const watchedDate = watch('logDate');

  const totals = useMemo(() => {
    const totalWorkers = watchedWorkforce?.reduce((sum, w) => sum + (w?.workerCount || 0), 0) || 0;
    const totalHours =
      watchedWorkforce?.reduce((sum, w) => sum + (w?.workerCount || 0) * (w?.hours || 0), 0) || 0;
    const totalDelayHours = watchedDelays?.reduce((sum, d) => sum + (d?.hoursLost || 0), 0) || 0;
    return { totalWorkers, totalHours, totalDelayHours };
  }, [watchedWorkforce, watchedDelays]);

  // Section counts for badges
  const sectionCounts = useMemo(
    () => ({
      workforce: workforceFieldArray.fields.length,
      equipment: equipmentFieldArray.fields.length,
      workPerformed: workPerformedFieldArray.fields.length,
      delays: delaysFieldArray.fields.length,
      visitors: visitorsFieldArray.fields.length,
      deliveries: deliveriesFieldArray.fields.length,
      photos: photosFieldArray.fields.length,
    }),
    [
      workforceFieldArray.fields.length,
      equipmentFieldArray.fields.length,
      workPerformedFieldArray.fields.length,
      delaysFieldArray.fields.length,
      visitorsFieldArray.fields.length,
      deliveriesFieldArray.fields.length,
      photosFieldArray.fields.length,
    ]
  );

  // Handle form save
  const handleFormSave = async (data: DailyLogFormData) => {
    if (!projectId) {
      showError('No project selected');
      return;
    }

    try {
      if (isCreateMode) {
        const createData: CreateDailyLogRequest = {
          logDate: data.logDate,
          weather: data.weather,
          workforce: data.workforce,
          equipment: data.equipment,
          workPerformed: data.workPerformed,
          delays: data.delays,
          visitors: data.visitors,
          deliveries: data.deliveries,
          safety: data.safety,
          generalNotes: data.generalNotes || undefined,
          issuesAndConcerns: data.issuesAndConcerns || undefined,
          tomorrowPlan: data.tomorrowPlan || undefined,
        };

        await createLog({ projectId, data: createData }).unwrap();
        success('Daily log created successfully');
      } else if (isEditMode && dailyLog) {
        const updateData: UpdateDailyLogRequest = {
          logDate: data.logDate,
          weather: data.weather,
          workforce: data.workforce,
          equipment: data.equipment,
          workPerformed: data.workPerformed,
          delays: data.delays,
          visitors: data.visitors,
          deliveries: data.deliveries,
          safety: data.safety,
          generalNotes: data.generalNotes || undefined,
          issuesAndConcerns: data.issuesAndConcerns || undefined,
          tomorrowPlan: data.tomorrowPlan || undefined,
        };

        await updateLog({ projectId, logId: dailyLog._id, data: updateData }).unwrap();
        success('Daily log updated successfully');
      }

      onSuccess?.();
      handleClose();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      showError(error.data?.message || 'Failed to save daily log');
    }
  };

  const handleSubmitForApproval = () => {
    setSignatureAction('submit');
    setShowSignatureModal(true);
  };

  const handleApprove = () => {
    setSignatureAction('approve');
    setShowSignatureModal(true);
  };

  const handleSignatureSubmit = async (signature: string) => {
    if (!projectId || !dailyLog) return;

    try {
      if (signatureAction === 'submit') {
        await submitLog({ projectId, logId: dailyLog._id, data: { signature } }).unwrap();
        success('Daily log submitted for approval');
      } else {
        await approveLog({ projectId, logId: dailyLog._id, data: { signature } }).unwrap();
        success('Daily log approved');
      }

      setShowSignatureModal(false);
      onSuccess?.();
      handleClose();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      showError(error.data?.message || `Failed to ${signatureAction} daily log`);
    }
  };

  const handleReject = async () => {
    if (!projectId || !dailyLog || !rejectReason.trim()) {
      showError('Rejection reason is required');
      return;
    }

    try {
      await rejectLog({ projectId, logId: dailyLog._id, data: { reason: rejectReason } }).unwrap();
      success('Daily log rejected');
      setShowRejectModal(false);
      setRejectReason('');
      onSuccess?.();
      handleClose();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      showError(error.data?.message || 'Failed to reject daily log');
    }
  };

  const handleDownloadPdf = async () => {
    if (!projectId || !dailyLog) return;
    try {
      await downloadDailyLogPdf(projectId, dailyLog._id, dailyLog.logNumber);
      success('PDF downloaded successfully');
    } catch {
      showError('Failed to download PDF');
    }
  };

  const handleClose = () => {
    if (!isProcessing) onClose();
  };

  if (!isOpen) return null;

  const canEdit = isCreateMode || (isEditMode && dailyLog?.status === 'draft');
  const canSubmit = dailyLog?.status === 'draft';
  const canApprove = dailyLog?.status === 'submitted';
  const canReject = dailyLog?.status === 'submitted';

  // Drawer width: compact (60%) for create, full (90%) for edit/view
  const drawerClass = isCreateMode
    ? 'fixed top-4 right-4 bottom-4 w-[60%] z-[100]'
    : 'fixed top-4 right-4 bottom-4 left-[8%] z-[100]';

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-[90]" onClick={handleClose} />

      {/* Drawer */}
      <div className={`${drawerClass} animate-in slide-in-from-right duration-200`}>
        <div className="bg-white rounded-xl shadow-2xl h-full flex flex-col overflow-hidden">
          {/* Compact Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-gray-900">
                    {isCreateMode
                      ? 'New Daily Log'
                      : isEditMode
                        ? 'Edit Daily Log'
                        : dailyLog?.logNumber}
                  </h2>
                  {dailyLog && (
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[dailyLog.status]}`}
                    >
                      {DAILY_LOG_STATUS_LABELS[dailyLog.status]}
                    </span>
                  )}
                </div>
                {isCreateMode && watchedDate && (
                  <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(watchedDate)}
                  </p>
                )}
                {dailyLog && !isCreateMode && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Day {dailyLog.dayNumber} • {formatDate(dailyLog.logDate)}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Stats Badge - Compact */}
              {totals.totalWorkers > 0 && (
                <div className="hidden sm:flex items-center gap-3 px-3 py-1.5 bg-gray-50 rounded-lg text-xs">
                  <span className="text-gray-600">
                    <span className="font-semibold text-gray-900">{totals.totalWorkers}</span>{' '}
                    workers
                  </span>
                  <span className="text-gray-300">•</span>
                  <span className="text-gray-600">
                    <span className="font-semibold text-gray-900">
                      {totals.totalHours.toFixed(0)}
                    </span>{' '}
                    hrs
                  </span>
                </div>
              )}

              {/* PDF Download */}
              {dailyLog && (
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Download PDF"
                >
                  <FileDown className="h-4 w-4" />
                </button>
              )}

              {/* Close */}
              <button
                type="button"
                onClick={handleClose}
                disabled={isProcessing}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit(handleFormSave)} className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto">
              <div className="px-6 py-4 space-y-3">
                {/* Date Picker - Only for create */}
                {isCreateMode && (
                  <div className="pb-3 border-b border-gray-100">
                    <Controller
                      name="logDate"
                      control={control}
                      render={({ field }) => (
                        <div className="flex items-center gap-3">
                          <label className="text-sm font-medium text-gray-700 w-20">Date</label>
                          <DatePicker
                            value={field.value}
                            onChange={date => field.onChange(date || '')}
                            maxDate={getTodayISO()}
                          />
                        </div>
                      )}
                    />
                  </div>
                )}

                {/* Rejection Notice */}
                {dailyLog?.status === 'rejected' && dailyLog.rejectionReason && (
                  <div className="bg-red-50 border border-red-100 rounded-lg p-3 flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-red-800">Rejected</p>
                      <p className="text-xs text-red-600">{dailyLog.rejectionReason}</p>
                    </div>
                  </div>
                )}

                {/* Section Navigation - Quick add buttons */}
                {isCreateMode && (
                  <div className="flex flex-wrap gap-1.5 pb-3 border-b border-gray-100">
                    {[
                      { key: 'weather', label: 'Weather', count: null },
                      { key: 'workforce', label: 'Workforce', count: sectionCounts.workforce },
                      { key: 'equipment', label: 'Equipment', count: sectionCounts.equipment },
                      { key: 'workPerformed', label: 'Work', count: sectionCounts.workPerformed },
                      { key: 'delays', label: 'Delays', count: sectionCounts.delays },
                      { key: 'visitors', label: 'Visitors', count: sectionCounts.visitors },
                      { key: 'deliveries', label: 'Deliveries', count: sectionCounts.deliveries },
                      { key: 'safety', label: 'Safety', count: null },
                      { key: 'photos', label: 'Photos', count: sectionCounts.photos },
                      { key: 'notes', label: 'Notes', count: null },
                    ].map(({ key, label, count }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => toggleSection(key as keyof typeof expandedSections)}
                        className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                          expandedSections[key as keyof typeof expandedSections]
                            ? 'bg-brand-100 text-brand-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {label}
                        {count !== null && count > 0 && (
                          <span className="bg-brand-600 text-white text-[10px] px-1 rounded">
                            {count}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Sections */}
                <div className="space-y-2">
                  <WeatherSection
                    control={control}
                    errors={errors}
                    isExpanded={expandedSections.weather}
                    onToggle={() => toggleSection('weather')}
                    disabled={!canEdit}
                  />

                  <WorkforceSection
                    control={control}
                    errors={errors}
                    fieldArray={workforceFieldArray}
                    isExpanded={expandedSections.workforce}
                    onToggle={() => toggleSection('workforce')}
                    disabled={!canEdit}
                  />

                  <EquipmentSection
                    control={control}
                    errors={errors}
                    fieldArray={equipmentFieldArray}
                    isExpanded={expandedSections.equipment}
                    onToggle={() => toggleSection('equipment')}
                    disabled={!canEdit}
                  />

                  <WorkPerformedSection
                    control={control}
                    errors={errors}
                    fieldArray={workPerformedFieldArray}
                    isExpanded={expandedSections.workPerformed}
                    onToggle={() => toggleSection('workPerformed')}
                    disabled={!canEdit}
                  />

                  <DelaysSection
                    control={control}
                    errors={errors}
                    fieldArray={delaysFieldArray}
                    isExpanded={expandedSections.delays}
                    onToggle={() => toggleSection('delays')}
                    disabled={!canEdit}
                  />

                  <VisitorsSection
                    control={control}
                    errors={errors}
                    fieldArray={visitorsFieldArray}
                    isExpanded={expandedSections.visitors}
                    onToggle={() => toggleSection('visitors')}
                    disabled={!canEdit}
                  />

                  <DeliveriesSection
                    control={control}
                    errors={errors}
                    fieldArray={deliveriesFieldArray}
                    isExpanded={expandedSections.deliveries}
                    onToggle={() => toggleSection('deliveries')}
                    disabled={!canEdit}
                  />

                  <SafetySection
                    control={control}
                    errors={errors}
                    isExpanded={expandedSections.safety}
                    onToggle={() => toggleSection('safety')}
                    disabled={!canEdit}
                  />

                  <PhotosSection
                    control={control}
                    errors={errors}
                    fieldArray={photosFieldArray}
                    isExpanded={expandedSections.photos}
                    onToggle={() => toggleSection('photos')}
                    disabled={!canEdit}
                  />

                  <NotesSection
                    control={control}
                    errors={errors}
                    isExpanded={expandedSections.notes}
                    onToggle={() => toggleSection('notes')}
                    disabled={!canEdit}
                  />
                </div>

                {/* Signature Info (View Mode) */}
                {dailyLog && (dailyLog.preparedBy || dailyLog.approvedBy) && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs font-medium text-gray-500 mb-2">Signatures</p>
                    <div className="grid grid-cols-2 gap-3">
                      {dailyLog.preparedBy && (
                        <div className="bg-gray-50 rounded-lg p-2.5">
                          <p className="text-[10px] text-gray-400 uppercase tracking-wide">
                            Prepared
                          </p>
                          <p className="text-xs font-medium text-gray-900">
                            {dailyLog.preparedBy.name}
                          </p>
                          <p className="text-[10px] text-gray-500">
                            {new Date(dailyLog.preparedBy.signedAt).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      {dailyLog.approvedBy && (
                        <div className="bg-green-50 rounded-lg p-2.5">
                          <p className="text-[10px] text-green-600 uppercase tracking-wide">
                            Approved
                          </p>
                          <p className="text-xs font-medium text-gray-900">
                            {dailyLog.approvedBy.name}
                          </p>
                          <p className="text-[10px] text-gray-500">
                            {new Date(dailyLog.approvedBy.signedAt).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Compact Footer */}
            <div className="border-t border-gray-100 px-6 py-3 bg-gray-50/50">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-400">
                  {dailyLog && `Updated ${new Date(dailyLog.updatedAt).toLocaleDateString()}`}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isProcessing}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {isViewMode ? 'Close' : 'Cancel'}
                  </button>

                  {canEdit && (
                    <button
                      type="submit"
                      disabled={isProcessing}
                      className="px-4 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                    >
                      {isSaving ? (
                        <>
                          <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <span>{isCreateMode ? 'Create Log' : 'Save'}</span>
                      )}
                    </button>
                  )}

                  {isViewMode && canSubmit && (
                    <button
                      type="button"
                      onClick={handleSubmitForApproval}
                      disabled={isProcessing}
                      className="px-4 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                    >
                      <Send className="h-3.5 w-3.5" />
                      <span>Submit</span>
                    </button>
                  )}

                  {isViewMode && canReject && (
                    <button
                      type="button"
                      onClick={() => setShowRejectModal(true)}
                      disabled={isProcessing}
                      className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1"
                    >
                      <XCircle className="h-3.5 w-3.5" />
                      <span>Reject</span>
                    </button>
                  )}

                  {isViewMode && canApprove && (
                    <button
                      type="button"
                      onClick={handleApprove}
                      disabled={isProcessing}
                      className="px-4 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                    >
                      <CheckCircle className="h-3.5 w-3.5" />
                      <span>Approve</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Signature Modal */}
      <SignatureCapture
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onSubmit={handleSignatureSubmit}
        title={signatureAction === 'submit' ? 'Submit Daily Log' : 'Approve Daily Log'}
        description={
          signatureAction === 'submit'
            ? 'Sign below to submit this daily log for approval.'
            : 'Sign below to approve this daily log.'
        }
        submitLabel={signatureAction === 'submit' ? 'Submit' : 'Approve'}
        isSubmitting={isSubmitting || isApproving}
        signerName={user?.name}
        signerRole={user?.role}
      />

      {/* Reject Modal */}
      {showRejectModal &&
        createPortal(
          <>
            <div
              className="fixed inset-0 bg-black/50 z-[150]"
              onClick={() => setShowRejectModal(false)}
            />
            <div className="fixed inset-0 z-[151] flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5">
                <h3 className="text-base font-semibold text-gray-900 mb-3">Reject Daily Log</h3>
                <textarea
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="Reason for rejection..."
                />
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowRejectModal(false);
                      setRejectReason('');
                    }}
                    disabled={isRejecting}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleReject}
                    disabled={!rejectReason.trim() || isRejecting}
                    className="px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {isRejecting ? 'Rejecting...' : 'Reject'}
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </>,
    document.body
  );
};

export default DailyLogDrawer;
